import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BillingChip } from "@/pages/Members";

const PLAN_LABEL = {
  pro_m: "Pro monthly", pro_y: "Pro yearly", elite_m: "Elite monthly", elite_y: "Elite yearly",
  pro_once: "Pro, one month", elite_once: "Elite, one month",
};
const STATUS = { setting_up: "setting up", retrying: "retrying", paid: "one-time" };

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/payments");
      setRows(data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load payments.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-[--w-bg] text-[--w-text]" data-testid="payments-page">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <Link to="/" data-testid="payments-back-link" className="mb-6 inline-flex items-center gap-1.5 text-sm text-[--w-text-2] transition-colors duration-200 hover:text-[--w-text]">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>
        <div className="flex items-end justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold text-[--w-text] sm:text-4xl">Payments</h1>
            <p className="mt-2 text-sm text-[--w-text-2]">Razorpay subscriptions and one-time payments started from WhatsApp.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="payments-refresh-button"><RefreshCw className="h-4 w-4" /></Button>
        </div>
        {error && <p data-testid="payment-error" className="mt-4 text-sm text-red-700">{error}</p>}

        <div className="mt-6 overflow-x-auto rounded-lg border border-[--w-border]" data-testid="payments-ledger">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-[--w-bg] text-left text-[11px] uppercase tracking-wide text-[--w-text-3]">
              <tr>
                <th className="px-4 py-2">Lawyer</th><th className="px-4 py-2">Plan</th><th className="px-4 py-2">Amount</th>
                <th className="px-4 py-2">Status</th><th className="px-4 py-2">Paid until</th><th className="px-4 py-2">Started</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[--w-text-3]">No payments yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`payment-row-${r.id}`} className="border-t border-[--w-border]">
                  <td className="px-4 py-2 font-mono-data text-[--w-text-2]">{r.phone || "-"}</td>
                  <td className="px-4 py-2">{PLAN_LABEL[r.opt] || r.plan || "Checkout"}</td>
                  <td className="px-4 py-2 font-mono-data">₹{((r.amount || 0) / 100).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2"><BillingChip status={STATUS[r.status] || r.status} testid={`payment-status-${r.id}`} /></td>
                  <td className="px-4 py-2 text-[--w-text-2]">{(r.paid_until || "").slice(0, 10) || "-"}</td>
                  <td className="px-4 py-2 text-xs text-[--w-text-3]">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
